import { Analytics } from '@easy-expense/analytics-client';
import { useIsAdminRole, useSubscriptionStore } from '@easy-expense/data-firestore-client';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { TEAM_MIN_SEATS } from '@easy-expense/utils-shared';
import type { PlanGateDecision, PlanGateSeats } from '@easy-expense/utils-shared';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { MeteredPaywallActions } from '../../hooks/useHandleMeteredAction';
import {
  INDIVIDUAL_MONTHLY_PRICES,
  INDIVIDUAL_TRIAL_DAYS,
  SETTINGS_BILLING_ADD_SEATS_ROUTE,
  SETTINGS_BILLING_ROUTE,
} from '../../lib/Billing/constants';
import { professionalPlanFeatures, starterPlanFeatures } from '../../lib/Billing/planFeatures';
import { Button } from '../Button.components';
import { Modal } from '../Shared/Modal.component';

type FeatureCopy = { emoji: string; feature: string; description: string };

/** The same emoji, feature name and description the mobile hard paywall shows for each gate. */
function useFeatureCopy(
  action: MeteredPaywallActions,
  decision: PlanGateDecision,
  seats: PlanGateSeats,
): FeatureCopy {
  if (decision === 'add-seats') {
    const seatsAllowed = seats.seatsAllowed ?? 0;
    const seatsLabel = seatsAllowed === 1 ? getTranslation('seat') : getTranslation('seats');
    const membersLabel =
      seats.seatsUsed === 1 ? getTranslation('member') : getTranslation('members');
    // Team and individual Professional share a tier; only a Team plan buys TEAM_MIN_SEATS or more.
    let nextStep = getTranslation('Upgrade to a Team plan to invite more members.');
    if (seatsAllowed >= TEAM_MIN_SEATS) {
      nextStep = getTranslation('Add seats to invite more members.');
    }
    return {
      emoji: '👥',
      feature: getTranslation('Add Seats'),
      description: `${getTranslation('Your plan includes')} ${seatsAllowed} ${seatsLabel} ${getTranslation('and you have')} ${seats.seatsUsed} ${membersLabel}. ${nextStep}`,
    };
  }
  switch (action) {
    case MeteredPaywallActions.SCAN_RECEIPT:
      return {
        emoji: '📸',
        feature: getTranslation('AI Receipt Scanner'),
        description: getTranslation('Save time with unlimited access to our smart receipt scanner'),
      };
    case MeteredPaywallActions.CREATE_REPORT:
      return {
        emoji: '📊',
        feature: getTranslation('Expense Reports'),
        description: getTranslation('Create the report with unlimited access to Expense Reports'),
      };
    case MeteredPaywallActions.REVIEW_BANK_TRANSACTION:
      return {
        emoji: '🏦',
        feature: getTranslation('Review Bank Transactions'),
        description: getTranslation(
          'Automatically track transactions from credit and debit cards!',
        ),
      };
    case MeteredPaywallActions.REVIEW_EMAIL_TRANSACTION:
      return {
        emoji: '📧',
        feature: getTranslation('Review Email Receipts'),
        description: getTranslation(
          'Automatically track receipts emailed to you. No more manual entry!',
        ),
      };
    case MeteredPaywallActions.MULTI_CURRENCY:
      return {
        emoji: '💱',
        feature: getTranslation('Multi Currency'),
        description: getTranslation(
          'Track purchases in any country and automatically convert to your home currency.',
        ),
      };
    case MeteredPaywallActions.LINK_MULTIPLE_ACCOUNTS:
      return {
        emoji: '🏦',
        feature: getTranslation('Link Multiple Accounts'),
        description: getTranslation(
          'Automatically track transactions from credit and debit cards!',
        ),
      };
    case MeteredPaywallActions.RECONCILE_STATEMENTS:
      return {
        emoji: '🧾',
        feature: getTranslation('Statement Reconciliation'),
        description: getTranslation(
          'Reconcile bank statements against your expenses with the Professional plan.',
        ),
      };
    case MeteredPaywallActions.CREATE_WORKSPACE:
      return {
        emoji: '💼',
        feature: getTranslation('Create Workspaces'),
        description: getTranslation(
          'Use workspaces to manage multiple businesses and keep data separated',
        ),
      };
    case MeteredPaywallActions.INVITE_MEMBER:
      return {
        emoji: '👥',
        feature: getTranslation('Invite Your Team'),
        description: getTranslation(
          'Invite team members to your workspace with the Professional plan.',
        ),
      };
  }
}

/**
 * The monthly-allowance line under a metered wall, worded for the action it blocked. Null when
 * there is no usage to state or the action has no allowance of its own.
 */
function usageLineFor(
  action: MeteredPaywallActions,
  usage?: { count: number; limit: number },
): string | null {
  if (!usage) {
    return null;
  }
  switch (action) {
    case MeteredPaywallActions.SCAN_RECEIPT:
      return `${getTranslation("You've used your")} ${usage.limit} ${getTranslation('free receipt scans this month.')} ${getTranslation('You can still enter the details manually.')}`;
    case MeteredPaywallActions.CREATE_REPORT:
      return `${getTranslation("You've used your")} ${usage.limit} ${getTranslation('free reports this month.')}`;
    case MeteredPaywallActions.REVIEW_BANK_TRANSACTION:
    case MeteredPaywallActions.REVIEW_EMAIL_TRANSACTION:
      return `${getTranslation("You've used your")} ${usage.limit} ${getTranslation('free bank and email transaction reviews this month.')}`;
    default:
      return null;
  }
}

/** A plan box with a floating label, as on the mobile Starter → Professional comparison. */
const PlanBox: React.FC<{
  label: string;
  features: string[];
  highlighted: boolean;
}> = ({ label, features, highlighted }) => (
  <Layout.Column
    border={[highlighted ? 2 : 1, 'solid', highlighted ? 'brandPrimary' : 'black']}
    bg={highlighted ? 'inputBackground' : 'grayXLight'}
    radius={8}
    px={16}
    py={6}
    style={{ position: 'relative' }}
  >
    <Layout.Column
      style={{ position: 'absolute', top: -12, left: 16 }}
      bg={highlighted ? 'brandPrimary' : 'black'}
      radius={100}
      px={8}
      py={2}
    >
      <OpenSans.Primary color="inverse" weight="bold-700" size="xs-12">
        {label}
      </OpenSans.Primary>
    </Layout.Column>
    <Spacer.Vertical size={16} />
    {features.map((feature) => (
      <OpenSans.Primary key={feature} size="s-16" style={{ paddingTop: 2, paddingBottom: 2 }}>
        {feature}
      </OpenSans.Primary>
    ))}
  </Layout.Column>
);

/** The block between the description and the buttons, mirroring mobile's CenterContent. */
const CenterContent: React.FC<{ hasStarterSubscription: boolean; trialDays: number }> = ({
  hasStarterSubscription,
  trialDays,
}) => {
  const { formatCurrency } = useIntlStore();

  if (hasStarterSubscription) {
    return (
      <Layout.Column style={{ width: '100%' }}>
        <PlanBox
          label={getTranslation('Your Current Plan: Starter')}
          features={starterPlanFeatures()}
          highlighted={false}
        />
        <Spacer.Vertical size={32} />
        <PlanBox
          label={getTranslation('Upgrade to: Professional')}
          features={professionalPlanFeatures()}
          highlighted
        />
      </Layout.Column>
    );
  }

  return (
    <Layout.Column align style={{ width: '100%' }}>
      <Layout.Column bg="brandPrimaryXLight" px={16} radius={20} py={6}>
        <OpenSans.Pressable weight="bold-700">
          {`${getTranslation('Start')} ${trialDays}-${getTranslation('Day Free Trial')}`}
        </OpenSans.Pressable>
      </Layout.Column>

      <Spacer.Vertical size={32} />

      <Layout.Row justify="space-between" style={{ width: '80%' }}>
        <Layout.Column style={{ whiteSpace: 'nowrap' }}>
          <OpenSans.Primary weight="bold-700">{getTranslation('JOIN OVER')}</OpenSans.Primary>
          <OpenSans.Primary size={28} weight="bold-700">
            40,000+
          </OpenSans.Primary>
          <OpenSans.Secondary>{getTranslation('businesses')}</OpenSans.Secondary>
        </Layout.Column>

        <Layout.Column style={{ whiteSpace: 'nowrap' }}>
          <OpenSans.Primary weight="bold-700">{getTranslation('AS LOW AS')}</OpenSans.Primary>
          <OpenSans.Primary size={28} weight="bold-700">
            {formatCurrency(INDIVIDUAL_MONTHLY_PRICES.PROFESSIONAL.annual)}
          </OpenSans.Primary>
          <OpenSans.Secondary>{getTranslation('per month')}</OpenSans.Secondary>
        </Layout.Column>
      </Layout.Row>
    </Layout.Column>
  );
};

/**
 * The wall a plan-gated web action shows, laid out like the mobile hard paywall's feature
 * screen: the feature's emoji, name and description, then the trial offer (or the Starter to
 * Professional comparison), then the way to billing. Members who cannot see billing are told to
 * ask their admin instead.
 */
export const UpgradeModal: React.FC<{
  action: MeteredPaywallActions;
  decision: PlanGateDecision;
  seats: PlanGateSeats;
  /** This month's count and the free allowance, shown for metered actions. */
  usage?: { count: number; limit: number };
  onClose: () => void;
}> = ({ action, decision, seats, usage, onClose }) => {
  const isAdmin = useIsAdminRole();
  const navigate = useNavigate();
  const hasStarterSubscription = useSubscriptionStore(
    (s) => s.hasSubscription && !s.hasProPlanSubscription,
  );
  const { emoji, feature, description } = useFeatureCopy(action, decision, seats);
  const usageLine = usageLineFor(action, usage);
  const isAddSeats = decision === 'add-seats';
  const trialDays = INDIVIDUAL_TRIAL_DAYS.PROFESSIONAL;

  React.useEffect(() => {
    if (hasStarterSubscription) {
      Analytics.track('upgrade_to_pro_plan_seen');
    }
    Analytics.track('paywall_feature_interacted', { feature, action: 'shown' });
  }, []);

  const goToBilling = (
    interaction: 'show_all' | 'add_seats',
    route: string = SETTINGS_BILLING_ROUTE,
  ) => {
    Analytics.track('paywall_feature_interacted', { feature, action: interaction });
    onClose();
    navigate(route);
  };

  // One button per wall: "Start Free Trial" opened the same billing page as "View All Plans".
  let adminButton = (
    <Button.Secondary
      variant="bold"
      align="center"
      content={getTranslation('View All Plans')}
      onClick={() => goToBilling('show_all')}
    />
  );
  if (isAddSeats) {
    // Lands on billing with the seat picker already open, not just the billing page.
    adminButton = (
      <Button.Primary
        variant="bold"
        align="center"
        content={getTranslation('Add Seats')}
        onClick={() => goToBilling('add_seats', SETTINGS_BILLING_ADD_SEATS_ROUTE)}
      />
    );
  }

  return (
    <Modal
      showModal
      setShowModal={(show) => {
        if (!show) {
          onClose();
        }
      }}
      footer={
        <Layout.Column px={32} py={24} style={{ gap: 12 }}>
          {isAdmin ? (
            adminButton
          ) : (
            <>
              <OpenSans.Secondary size="s-16" style={{ textAlign: 'center' }}>
                {getTranslation('Ask your workspace admin to upgrade the plan.')}
              </OpenSans.Secondary>
              <Button.Primary
                variant="bold"
                align="center"
                content={getTranslation('OK')}
                onClick={onClose}
              />
            </>
          )}
        </Layout.Column>
      }
    >
      <Layout.Column center style={{ width: 480, maxWidth: 'calc(100vw - 96px)' }}>
        <OpenSans.Primary size={64} style={{ textAlign: 'center', lineHeight: '72px' }}>
          {emoji}
        </OpenSans.Primary>

        <Spacer.Vertical size={16} />

        <OpenSans.Primary size={28} weight="bold-700" style={{ textAlign: 'center' }}>
          {hasStarterSubscription && !isAddSeats ? `${getTranslation('Upgrade to')} ` : ''}
          {feature}
        </OpenSans.Primary>
        <Spacer.Vertical size={16} />
        <OpenSans.Primary style={{ textAlign: 'center' }}>{description}</OpenSans.Primary>
        {usageLine ? (
          <>
            <Spacer.Vertical size={8} />
            <OpenSans.Secondary style={{ textAlign: 'center' }}>{usageLine}</OpenSans.Secondary>
          </>
        ) : null}

        {isAddSeats ? null : (
          <>
            <Spacer.Vertical size={32} />
            <CenterContent hasStarterSubscription={hasStarterSubscription} trialDays={trialDays} />
          </>
        )}
        <Spacer.Vertical size={8} />
      </Layout.Column>
    </Modal>
  );
};
